import { useTranslation } from 'react-i18next';
import { useFilterStore } from '../store/filter-store';
import { Switch } from '@/components/ui/Switch';
import type { LocationInformation } from '@/features/location/types/location-info.types';

interface LocationBlockProps {
  locationInfo: LocationInformation | undefined;
}

/**
 * Consolidated, collapsible location-context card pinned near the top of the
 * filter panel. The detected location drives the operators list and the price
 * currency. Minimized → just the country header; expanded → the "show pin"
 * toggle plus the address & coordinates (always shown when expanded).
 */
export function LocationBlock({ locationInfo }: LocationBlockProps) {
  const { t } = useTranslation();
  const showAnchorPoint = useFilterStore((s) => s.showAnchorPoint);
  const anchorPointVisible = useFilterStore((s) => s.anchorPointVisible);
  const setAnchorPointVisible = useFilterStore((s) => s.setAnchorPointVisible);
  const isExpanded = useFilterStore((s) => s.isLocationExpanded);
  const setLocationExpanded = useFilterStore((s) => s.setLocationExpanded);

  // The query returns `undefined` while loading and a defined object once
  // resolved — even for invalid spots (e.g. the ocean) where no country
  // matched. Distinguish "still loading" from "loaded, no country" so we
  // don't get stuck showing "Loading...".
  const isLoading = locationInfo === undefined;
  const hasCountry = !!locationInfo?.countryName;
  const hasDetails =
    !!locationInfo?.formattedAddress || !!locationInfo?.coordinates;

  return (
    <div
      data-testid="location-block"
      className="rounded-xl border border-ink/10 bg-ink/[0.03] p-3"
    >
      {/* Header - click to minimize/expand the whole card */}
      <button
        type="button"
        onClick={() => setLocationExpanded(!isExpanded)}
        className="w-full flex items-center justify-between gap-2 text-left"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2 min-w-0">
          {isLoading ? (
            <>
              <div className="w-5 h-4 bg-ink/15 rounded-sm animate-pulse" />
              <span className="text-sm font-medium text-muted">
                {t('filters.location.loading')}
              </span>
            </>
          ) : hasCountry ? (
            <>
              {locationInfo.countryCode && (
                <img
                  src={`https://monta.imgix.net/flags/${locationInfo.countryCode.toLowerCase()}.png`}
                  alt={locationInfo.countryName}
                  className="w-5 h-4 object-cover rounded-sm shadow-sm flex-shrink-0"
                />
              )}
              <span className="text-sm font-medium text-ink/80 truncate">
                {locationInfo.countryName}
              </span>
            </>
          ) : (
            <>
              {/* Loaded, but no country matched (e.g. ocean / invalid spot) */}
              <svg
                className="w-4 h-4 flex-shrink-0 text-muted/70"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z M3.6 9h16.8 M3.6 15h16.8 M12 3a15 15 0 010 18 M12 3a15 15 0 000 18"
                />
              </svg>
              <span className="text-sm font-medium text-ink/80">
                {t('common.status.unknown')}
              </span>
            </>
          )}
        </div>
        <svg
          className={`w-4 h-4 flex-shrink-0 text-muted transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Expanded body: pin toggle + address & coordinates */}
      {isExpanded && (
        <div className="mt-3 space-y-3">
          {showAnchorPoint && (
            <Switch
              checked={anchorPointVisible}
              onCheckedChange={setAnchorPointVisible}
              label={t('filters.location.showPin')}
            />
          )}

          {isLoading ? (
            // Reserve the address/coordinates height while the first load
            // resolves, so the card doesn't grow and jump when data arrives.
            <div className="space-y-1.5" aria-hidden="true">
              <div className="h-3.5 w-3/4 rounded bg-ink/10 animate-pulse" />
              <div className="h-3.5 w-1/2 rounded bg-ink/10 animate-pulse" />
            </div>
          ) : hasDetails ? (
            <div className="space-y-1 text-xs text-muted">
              {locationInfo?.formattedAddress && (
                <div className="flex items-start gap-1.5">
                  <svg
                    className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-muted"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="break-all">
                    {locationInfo.formattedAddress}
                  </span>
                </div>
              )}
              {locationInfo?.coordinates && (
                <div className="flex items-center gap-1.5">
                  <svg
                    className="w-3.5 h-3.5 flex-shrink-0 text-muted"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                    />
                  </svg>
                  <span className="font-mono">
                    {locationInfo.coordinates.lat.toFixed(6)},{' '}
                    {locationInfo.coordinates.lng.toFixed(6)}
                  </span>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
